import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { graphQlClient, graphQlUpload } from '@config/graphqlClient';
import EmployeesGraphQL from '@graphql/employee.queries';
import { EMPLOYEES_QUERY_KEY } from '@hooks/api/employees/useEmployees';
import { store } from '@redux/store';
import { normalizeEmail } from '@utils/normalizeEmail';
import { format } from 'date-fns';
import { getErrorMessage, isNoSeatsAvailableError } from '@utils/errorMessage';
import { useTranslation } from '@hooks/useTranslation';

export type CreateUserArgs = {
  firstName: string;
  lastName: string;
  email: string;
  dob?: string | Date | null;
  hireDate?: string | Date | null;
  rate?: number | null;
  payPeriod?: string | null;
  mode?: string | null;
  role?: string | null; // roleType in legacy API
  managerProjectIds?: string[] | null;
  superintendentProjectIds?: string[] | null;
  reviewerProjectIds?: string[] | null;
  file?: File | string | null; // Prefer File (legacy uses upload); string (base64) supported for backward compatibility
};

export const useCreateEmployee = () => {
  const tActionMessages = useTranslation('actionMessages');
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['users', 'create'],
    mutationFn: async (payload: CreateUserArgs) => {
      // Mirror legacy createOrUpdateProject payload shape
      const { userId: currentUserId } = store.getState().user || ({} as any);
      const localDate = new Date();

      const managerProjectsIds =
        (payload.managerProjectIds ?? []).length > 0 &&
        payload.role === 'PROJECT_MANAGER'
          ? payload.managerProjectIds
          : null;
      const superintendentProjectsIds =
        (payload.superintendentProjectIds ?? []).length > 0 &&
        payload.role === 'SUPERINTENDENT'
          ? payload.superintendentProjectIds
          : null;
      const reviewerProjectsIds =
        (payload.reviewerProjectIds ?? []).length > 0 &&
        payload.role === 'REVIEWER'
          ? payload.reviewerProjectIds
          : null;

      const employeeInput: any = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: normalizeEmail(payload.email),
        dob: payload.dob ? format(payload.dob as Date, 'yyyy-MM-dd') : null,
        hireDate: payload.hireDate
          ? format(payload.hireDate as Date, 'yyyy-MM-dd')
          : null,
        rate: payload.rate ?? null,
        selectedPayPeriod: payload.payPeriod ?? null,
        selectedMode: payload.mode ?? null,
        roleType: payload.role ?? null,
        currentUserId,
        localDate: format(localDate, 'yyyy-MM-dd'),
        managerProjectsIds,
        superintendentProjectsIds,
        reviewerProjectsIds,
      };

      const query = EmployeesGraphQL.mutations.createEmployee as any;

      let result;
      // Prefer true multipart upload if a File is provided; fallback to plain request
      if (payload.file && typeof payload.file !== 'string') {
        result = await graphQlUpload(
          payload.file as File,
          'employee',
          query,
          employeeInput,
        );
      } else {
        if (payload.file && typeof payload.file === 'string') {
          employeeInput.file = payload.file;
        }
        result = await graphQlClient.request(query, {
          employee: employeeInput,
        });
      }

      // Return both the result and the role for navigation
      return { result, role: payload.role };
    },
    onSuccess: () => {
      toast.success(tActionMessages.userCreatedSuccessfully);
      queryClient.invalidateQueries({ queryKey: EMPLOYEES_QUERY_KEY });
      // Invalidate active seats to update seats counter on Users page
      queryClient.invalidateQueries({
        queryKey: ['activeSeats'],
        refetchType: 'all',
      });
    },
    onError: (error: any) => {
      // Sprint 20, story-093: the caller's onSeatsExhausted handler already opens the
      // out-of-seats modal for this code — the modal is the explanation, no toast needed.
      if (isNoSeatsAvailableError(error)) return;
      toast.error(getErrorMessage(error, tActionMessages.requestError));
    },
  });
};
